import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ScrollArea,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { useReleaseNotes } from '@ais-ui/api';
import { getNewestReleaseNotes, WordpressReleaseNote } from '@ais-ui/utils';
import { ReleaseNotesContent } from './ReleaseNotesContent';
import { useReleaseNotesContext } from './ReleaseNotesProvider';
import { PiCaretLeft, PiCaretRight } from 'react-icons/pi';
import { ErrorDisplay } from '../../components/ErrorDisplay';

function useHaveSeenReleaseNotes(
  releaseNotes: WordpressReleaseNote[],
  lastSeenCookieName: string
) {
  const [hasSeen, setHasSeen] = useState(true);

  useEffect(() => {
    if (!releaseNotes || releaseNotes.length === 0) return;

    // Get the newest release
    const newestRelease = getNewestReleaseNotes(releaseNotes);
    if (!newestRelease) return;

    // Find the ID of the latest release seen by the user
    const newestSeenReleaseId: string | undefined = document.cookie
      .split('; ')
      .find((row) => row.startsWith(lastSeenCookieName))
      ?.split('=')[1];

    if (Number(newestSeenReleaseId) !== newestRelease.id) {
      setHasSeen(false);
    }
  }, [releaseNotes, lastSeenCookieName]);

  return hasSeen;
}

export function ReleaseNotes() {
  const [shouldFetchReleaseNotes, setShouldFetchReleaseNotes] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data, error } = useReleaseNotes(!shouldFetchReleaseNotes);

  const releaseNotes = useMemo(() => {
    if (!data) return [];
    const sortedReleaseNotes = data.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    return sortedReleaseNotes;
  }, [data]);

  const currentReleaseNote = useMemo(
    () => releaseNotes?.[currentIndex] ?? null,
    [releaseNotes, currentIndex]
  );

  const lastSeenCookieName = 'lastSeenReleaseNotes';

  useEffect(() => {
    if (shouldFetchReleaseNotes && releaseNotes !== undefined) {
      setShouldFetchReleaseNotes(false);
    }
  }, [releaseNotes, shouldFetchReleaseNotes]);

  // Get the newest release notes
  const newestRelease = useMemo(
    () => getNewestReleaseNotes(releaseNotes),
    [releaseNotes]
  );

  useEffect(() => {
    if (newestRelease && releaseNotes) {
      // Find and set the index of the newest release
      const newestIndex = releaseNotes.findIndex(
        (releaseNote: WordpressReleaseNote) =>
          releaseNote.id === newestRelease.id
      );
      setCurrentIndex(newestIndex >= 0 ? newestIndex : 0);
    }
  }, [newestRelease, releaseNotes]);

  const hasSeen = useHaveSeenReleaseNotes(releaseNotes, lastSeenCookieName);
  const { open, setOpen } = useReleaseNotesContext();

  useEffect(() => {
    if (!hasSeen && newestRelease) {
      setOpen(true);
    }
  }, [hasSeen, newestRelease, setOpen]);

  const handleClose = () => {
    setOpen(false);

    const date = new Date();
    date.setFullYear(date.getFullYear() + 10);

    if (!newestRelease) return;

    document.cookie =
      lastSeenCookieName +
      '=' +
      newestRelease.id +
      '; expires=' +
      date.toUTCString() +
      '; path=/' +
      '; SameSite=Lax';
  };

  const handlePrevious = () => {
    if (!releaseNotes || currentIndex <= 0) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (!releaseNotes || currentIndex >= releaseNotes.length - 1) return;
    setCurrentIndex((prev) => prev + 1);
  };

  if (error) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="p-4 lg:p-6 xl:p-10 flex flex-col max-w-[650px] lg:max-w-[750px] xl:max-w-[1000px] max-h-[75%] bg-gradient-to-b from-background to-slate-100">
          <ErrorDisplay message="Unable to load release notes. Please try again later.">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </ErrorDisplay>
        </DialogContent>
      </Dialog>
    );
  }

  if (!currentReleaseNote) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="p-4 lg:p-6 xl:p-10 flex flex-col max-w-[650px] lg:max-w-[750px] xl:max-w-[1000px] max-h-[75%] bg-gradient-to-b from-background to-slate-100 border-0 border-t-4 border-primary">
        <DialogHeader className="flex flex-row items-center justify-between">
          <div className="flex-1">
            <DialogTitle id="releaseNotes-title" className="mb-2">
              {currentReleaseNote.title}
            </DialogTitle>
            <DialogDescription>{currentReleaseNote.subtitle}</DialogDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevious}
              disabled={currentIndex <= 0}
              className="h-8 w-8"
            >
              <PiCaretLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              disabled={
                !releaseNotes || currentIndex >= releaseNotes.length - 1
              }
              className="h-8 w-8"
            >
              <PiCaretRight className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        <ScrollArea 
          className="h-[300px] lg:h-[400px] xl:h-[450px]"
          observeResize={false}
          >
          <ReleaseNotesContent releaseNotes={currentReleaseNote} />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
